import {
  Box,
  Button,
  ButtonProps,
  keyframes,
  styled,
  Tooltip,
} from "@mui/material";
import { memo } from "react";
import Row from "./Row";
import { MediaType } from "types/file";
import { formatImgUrl } from "utils/helpers";
import { checkFileIsImage, createDownloadLink } from "helpers/file";
import { AddCircleOutline, AttachFile } from "@mui/icons-material";
import { useLoading } from "providers/loading";
import { apiUploadFile } from "services/api/file";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { API_DOMAIN } from "constants/schemas";
import { useIntl } from "react-intl";
import { ArrowUpIcon } from "Assets/icons";
import { showNotification } from "helpers/notification";
import { Img } from "./Img";

const ALLOWED_FILE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "application/pdf",
];

const ALLOWED_EXTENSIONS = [
  "png",
  "jpeg",
  "jpg",
  "webp",
  "PNG",
  "JPEG",
  "JPG",
  "WEBP",
  "pdf",
  "PDF",
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 4;

type Props = {
  medias?: MediaType[];
  setMedias?: (medias?: any) => void;
  inputId?: string;
  signle?: boolean;
  signleMedia?: string;
  isPrivate?: boolean;
  disabled?: boolean;
};

const UploadFiles = ({
  medias = [],
  setMedias,
  inputId = "media-input",
  signle = false,
  signleMedia = "",
  isPrivate = false,
  disabled = false,
}: Props) => {
  const { setLoading, loading } = useLoading();
  const t = useIntl();

  const onClickUploadFile = () => {
    if (!disabled) {
      document.getElementById(inputId)?.click();
    }
  };

  const handleFileChange = async (event: any, type: string) => {
    const file = event.target.files[0];
    if (file) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      // Check file type and extension
      if (
        !ALLOWED_FILE_TYPES.includes(file.type) ||
        !ALLOWED_EXTENSIONS.includes(fileExtension || "")
      ) {
        showNotification(
          t.formatMessage(
            {
              id: "file_type_not_supported",
            },
            {
              types: ALLOWED_EXTENSIONS.map((ext) => ext.toUpperCase()).join(
                ", "
              ),
            }
          )
        );
        event.target.value = "";
        return;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        showNotification(
          t.formatMessage(
            {
              id: "file_type_less_than",
            },
            {
              numberMb: "5mb",
            }
          )
        );
        event.target.value = "";
        return;
      }

      // Check number of files
      if (!signle && medias.length >= MAX_FILES) {
        showNotification(
          t.formatMessage(
            {
              id: "file_upload_limit_exceeded",
            },
            {
              numberfile: "4",
            }
          )
        );
        event.target.value = "";
        return;
      }

      setLoading(true);
      let formData = new FormData();
      formData.append("file", file);
      if (isPrivate) {
        formData.append("is_private", "true");
      }

      apiUploadFile(formData)
        .then((res) => {
          let { file_url, name } = res.data?.message || {};
          let newMedias: MediaType[];
          if (signle) {
            setMedias?.(file_url);
          } else {
            let newMedia: MediaType = { url: file_url, file: name };
            newMedias = [...medias, newMedia];
            setMedias?.(newMedias);
          }
          setLoading(false);
          event.target.value = "";
        })
        .catch((err) => {
          console.log(err);
          let errorMessage = t.formatMessage({
            id: "error_upload_file_up_server",
          });

          if (err.response?.data?.message) {
            const serverMessage = err.response.data.message;
            if (typeof serverMessage === "string") {
              try {
                const parsedMessage = JSON.parse(serverMessage);
                if (parsedMessage.title && parsedMessage.message) {
                  errorMessage = parsedMessage.message;
                }
              } catch (e) {
                errorMessage = serverMessage;
              }
            }
          }

          showNotification(errorMessage);
          setLoading(false);
          event.target.value = "";
        });
    }
  };

  const onClickClear = (e: any, i?: number) => {
    e.stopPropagation();
    if (signle) {
      setMedias?.(null);
    } else {
      const newMedias = medias.filter((_, id) => id !== i);
      setMedias?.(newMedias);
    }
  };

  const onClickMedia = (mediaUrl: string) => {
    if (!disabled) {
      if (mediaUrl) {
        let link = `${API_DOMAIN}${mediaUrl}`;
        createDownloadLink(link, mediaUrl);
      } else {
        onClickUploadFile();
      }
    }
  };

  const acceptString = [
    ...ALLOWED_FILE_TYPES,
    ...ALLOWED_EXTENSIONS.map((ext) => "." + ext.toLowerCase()),
  ].join(",");

  const tooltipText = `${t.formatMessage({
    id: "accept_file_types",
  })}: ${ALLOWED_EXTENSIONS.map((ext) => ext.toUpperCase()).join(
    ", "
  )}. \n${t.formatMessage({ id: "max_size" })}: 10MB.`;

  return (
    <StyledRow flexWrap="wrap">
      {(signle ? [{ url: signleMedia }] : medias).map((media, i) => {
        if (media.url)
          return (
            <Box
              className="app_media_box"
              key={i}
              onClick={() => onClickMedia(media.url)}
            >
              {media.url ? (
                checkFileIsImage(media.url) ? (
                  <Img
                    src={formatImgUrl(media.url, 1)}
                    loading="lazy"
                    alt="media box"
                  />
                ) : (
                  <AttachFile />
                )
              ) : (
                signle && <AddCircleOutline />
              )}

              {media.url && !disabled && (
                <IoMdCloseCircleOutline
                  className="clear-icon"
                  onClick={(e) => onClickClear(e, i)}
                />
              )}
            </Box>
          );
        return (
          <Tooltip title={tooltipText} arrow>
            <span>
              <UploadButton onClick={onClickUploadFile} loading={loading} />
            </span>
          </Tooltip>
        );
      })}

      {!signle && (
        <Tooltip title={tooltipText} arrow>
          <span>
            <UploadButton onClick={onClickUploadFile} loading={loading} />
          </span>
        </Tooltip>
      )}
      <input
        id={inputId}
        className="hidden"
        type="file"
        onChange={(e) => handleFileChange(e, "medias")}
        accept={acceptString}
      />
    </StyledRow>
  );
};

const UploadButton = (props: ButtonProps & { loading?: boolean }) => {
  const t = useIntl();
  const { loading, ...rest } = props;

  return (
    <Button
      className={`upload-btn ${loading ? "uploading" : ""}`}
      startIcon={<ArrowUpIcon />}
      {...rest}
    >
      {t.formatMessage({ id: "upload_file" })}
    </Button>
  );
};

const uploadEffect = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: 200px 0; }
`;

const StyledRow = styled(Row)(({ theme }) => ({
  ".app_media_box": {
    position: "relative",
    width: "60px",
    height: "60px",
    borderRadius: "8px",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "var(--gray-100)",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      objectPosition: "center",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "32px",
      color: "var(--gray-500)",
    }
  },
  ".clear-icon": {
    position: "absolute",
    right: 2,
    top: 2,
    color: "#000",
    cursor: "pointer",
    fontSize: 24,
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    borderRadius: "50%",
    padding: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.9)",
    }
  },
  ".upload-btn": {
    fontSize: 14,
    fontWeight: 500,
    backgroundColor: "var(--gray-100)",
    padding: "8px 10px",
    borderRadius: 16,
    color: "var(--gray-700)",
    textTransform: "initial",
    lineHeight: "20px",
    svg: {
      width: 10,
      heigth: 10,
    },
    "&.uploading": {
      background:
        "linear-gradient(90deg, #e0f7fa 25%, #80deea 50%, #e0f7fa 75%)",
      backgroundSize: "200% 100%",
      animation: `${uploadEffect} 1.5s infinite`,
    },
  },
  [theme.breakpoints.down("md")]: {
    ".upload-btn": {
      fontSize: 12,
    },
  },
}));

export default memo(UploadFiles);
